/**
 * Resizes an image file to a maximum dimension (e.g. 800px) and compresses it to JPEG
 * so that base64 strings stay under ~60KB. This prevents Firestore 1MB document limit errors
 * and ensures instant saving and rendering across all devices.
 */
export function compressImageFile(file: File, maxDimension: number = 600, quality: number = 0.75): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('Yüklenen dosya bir görsel değil.'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const rawDataUrl = e.target?.result as string;
      compressBase64Image(rawDataUrl, maxDimension, quality)
        .then(resolve)
        .catch(() => resolve(rawDataUrl));
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
}

/**
 * Resizes and compresses any Base64 data URL to JPEG (max 600px)
 * so that base64 strings stay under ~60KB.
 * Includes fallback re-compression so images are NEVER dropped/erased.
 */
export function compressBase64Image(dataUrl: string, maxDimension: number = 600, quality: number = 0.75): Promise<string> {
  return new Promise((resolve) => {
    if (!dataUrl || !dataUrl.startsWith('data:image')) {
      resolve(dataUrl);
      return;
    }

    const timer = setTimeout(() => resolve(dataUrl), 8000);

    const img = new Image();
    img.onload = () => {
      clearTimeout(timer);
      try {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          let compressed = canvas.toDataURL('image/jpeg', quality);

          // If compressed result is still over 300KB, perform a second pass to reduce size
          if (compressed.length > 300 * 1024) {
            const secondCanvas = document.createElement('canvas');
            const targetW = Math.round(width * 0.7);
            const targetH = Math.round(height * 0.7);
            secondCanvas.width = targetW;
            secondCanvas.height = targetH;
            const ctx2 = secondCanvas.getContext('2d');
            if (ctx2) {
              ctx2.drawImage(img, 0, 0, targetW, targetH);
              compressed = secondCanvas.toDataURL('image/jpeg', 0.60);
            }
          }
          resolve(compressed);
        } else {
          resolve(dataUrl);
        }
      } catch (err) {
        resolve(dataUrl);
      }
    };

    img.onerror = () => {
      clearTimeout(timer);
      resolve(dataUrl);
    };

    img.src = dataUrl;
  });
}
